// src/components/AdminXmasGreetings.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { apiFetch } from '../api';

export default function AdminXmasGreetings() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]       = useState('');
  const [filter, setFilter] = useState('pending'); // 'pending' | 'all'

  const loadGreetings = async () => {
    try {
      setLoading(true);
      setErr('');
      const res = await apiFetch('xmas_admin_greetings.php?action=list');
      const data = await res.json();
      if (Array.isArray(data)) {
        setItems(data);
      } else if (data.error) {
        throw new Error(data.error);
      } else {
        throw new Error('Unerwartete Antwort vom Server');
      }
    } catch (e) {
      console.error(e);
      setErr(e.message || 'Fehler beim Laden der Grüße');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGreetings();
  }, []);

  const approveGreeting = async (id, approved) => {
    try {
      const res = await apiFetch('xmas_admin_greetings.php', {
        method: 'POST',
        body: JSON.stringify({ action: 'approve', id, approved })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      // lokal updaten
      setItems(prev =>
        prev.map(item =>
          item.id === id ? { ...item, approved: approved ? 1 : 0 } : item
        )
      );
    } catch (e) {
      alert(e.message || 'Fehler beim Aktualisieren');
    }
  };

  const deleteGreeting = async (id) => {
    if (!window.confirm('Diesen Weihnachtsgruß wirklich löschen?')) return;
    try {
      const res = await apiFetch('xmas_admin_greetings.php', {
        method: 'POST',
        body: JSON.stringify({ action: 'delete', id })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      setItems(prev => prev.filter(item => item.id !== id));
    } catch (e) {
      alert(e.message || 'Fehler beim Löschen');
    }
  };

  const filteredItems = useMemo(() => {
    if (filter === 'pending') {
      return items.filter(i => !Number(i.approved));
    }
    return items;
  }, [items, filter]);

  const pendingCount = items.filter(i => !Number(i.approved)).length;
  const approvedCount = items.filter(i => Number(i.approved)).length;

  return (
    <div>
      <h2>Weihnachtsgrüße verwalten 🎄</h2>
      <p>Hier kannst du neue Grüße freigeben oder löschen.</p>

      <div style={{ margin: '0.5rem 0 1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button
          className={filter === 'pending' ? 'primary' : 'secondary'}
          onClick={() => setFilter('pending')}
        >
          Wartend ({pendingCount})
        </button>
        <button
          className={filter === 'all' ? 'primary' : 'secondary'}
          onClick={() => setFilter('all')}
        >
          Alle ({items.length}) – freigegeben: {approvedCount}
        </button>
        <button className="secondary" onClick={loadGreetings}>
          Neu laden
        </button>
      </div>

      {err && (
        <div className="card" style={{ color: 'crimson', borderColor: 'crimson', marginBottom: '1rem' }}>
          {err}
        </div>
      )}

      {loading && <div className="card">Lade Weihnachtsgrüße…</div>}

      {!loading && !err && filteredItems.length === 0 && (
        <div className="card" style={{ opacity: 0.8 }}>
          {filter === 'pending'
            ? 'Aktuell warten keine neuen Grüße auf Freigabe ✨'
            : 'Keine Einträge gefunden.'}
        </div>
      )}

      {!loading && !err && filteredItems.length > 0 && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px' }}>ID</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Datum</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Gruß</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => (
                <tr key={item.id} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ padding: '6px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{item.id}</td>
                  <td style={{ padding: '6px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    {item.created_at}
                  </td>
                  <td style={{ padding: '6px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    {item.name || <span style={{ opacity: 0.6 }}>(ohne Name)</span>}
                  </td>
                  <td style={{ padding: '6px', verticalAlign: 'top', maxWidth: 320 }}>
                    {item.message}
                  </td>
                  <td style={{ padding: '6px', verticalAlign: 'top' }}>
                    {Number(item.approved) ? (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: '#16a34a',
                        color: '#fff',
                        fontSize: 12
                      }}>Freigegeben</span>
                    ) : (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: '#f97316',
                        color: '#fff',
                        fontSize: 12
                      }}>Wartet</span>
                    )}
                  </td>
                  <td style={{ padding: '6px', verticalAlign: 'top' }}>
                    {!Number(item.approved) ? (
                      <button
                        className="primary"
                        style={{ marginRight: 6, marginBottom: 4 }}
                        onClick={() => approveGreeting(item.id, 1)}
                      >
                        Freigeben
                      </button>
                    ) : (
                      <button
                        className="secondary"
                        style={{ marginRight: 6, marginBottom: 4 }}
                        onClick={() => approveGreeting(item.id, 0)}
                      >
                        Zurückziehen
                      </button>
                    )}
                    <button
                      className="secondary"
                      style={{ background: '#dc2626', color: '#fff' }}
                      onClick={() => deleteGreeting(item.id)}
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}